import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslations } from '@/i18n/translations';
import {
  Body,
  Button,
  Heading,
  Dialog,
  DialogFooter,
  DialogSection,
  Stack,
} from '@leanin/ui';
import { FilterGroupLabelContext } from './filter-group-label';

// Buffers selections locally while the sheet is open and commits them to
// the parent in one shot on Apply. Live URL writes per pill click race —
// `useFilterParams.set` reads the URL-derived `parsed` state inside a
// transition, so two fast clicks both merge over the pre-click URL and
// the second wins, silently dropping the first selection.
export type FilterSheetProps<T> = {
  /** Panel id; the FilterButton that opens the sheet names it in
   *  `aria-controls`. */
  id: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  values: T;
  emptyValues: T;
  onApply: (next: T) => void;
  primaryLabel?: string;
  secondaryLabel?: string;
  children: (state: T, setState: (next: T) => void) => ReactNode;
};

export function FilterSheet<T>({
  id,
  open,
  onOpenChange,
  title,
  description,
  values,
  emptyValues,
  onApply,
  primaryLabel,
  secondaryLabel,
  children,
}: FilterSheetProps<T>) {
  const t = useTranslations('Common');
  const [draft, setDraft] = useState<T>(values);

  // Snapshot `values` on open so the draft starts from whatever the URL
  // currently reflects. Re-snapshotting on every `values` change would
  // wipe in-progress edits if the parent re-renders mid-edit.
  useEffect(() => {
    if (open) setDraft(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const apply = () => {
    onApply(draft);
    onOpenChange(false);
  };

  return (
    <Dialog
      id={id}
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
    >
      <DialogSection scrollable gap="lg">
        {children(draft, setDraft)}
      </DialogSection>
      <DialogFooter>
        <Button
          variant="ghost"
          type="button"
          onClick={() => setDraft(emptyValues)}
        >
          {secondaryLabel ?? t('widgets.filters.clearAll')}
        </Button>
        <Button variant="primary" type="button" onClick={apply}>
          {primaryLabel ?? t('widgets.filters.apply')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export type FilterGroupProps = {
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
};

// The heading names a pill set inside the group (PillFilter reads the id).
export function FilterGroup({ label, hint, children }: FilterGroupProps) {
  const headingId = useId();
  return (
    <Stack gap="sm">
      <Heading level={5} id={headingId}>{label}</Heading>
      {hint ? (
        <Body size="sm" color="mute">
          {hint}
        </Body>
      ) : null}
      <FilterGroupLabelContext.Provider value={headingId}>{children}</FilterGroupLabelContext.Provider>
    </Stack>
  );
}
